import React, { FormEvent, useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { LockIcon, XIcon } from 'lucide-react';
import { ConsentCheckbox } from './ConsentCheckbox';
import { saveSubmission, SUBMISSION_ERROR } from '../utils/submissions';
type Method = 'email' | 'phone';
type Status = 'idle' | 'sending' | 'done';
const ease = [0.22, 1, 0.36, 1] as const;
import { DialCodeSelect } from './DialCodeSelect';
import { useDialCode } from '../hooks/useDialCode';
type InviteWallProps = {
  title?: string;
  body?: string;
  onClose?: () => void;
};
export function InviteWall({
  title = 'The rest of this story is kept in confidence.',
  body = 'Superluxury news is read in full by members alone. Leave a way to reach you and, should an introduction be appropriate, we will extend a private invitation.',
  onClose
}: InviteWallProps) {
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const [method, setMethod] = useState<Method>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useDialCode();
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setConsentError(!consent);
    if (!consent) return;
    if (method === 'email' && !/^\S+@\S+\.\S+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (method === 'phone' && phone.replace(/\D/g, '').length < 6) {
      setError('Please enter a valid phone number.');
      return;
    }
    setError('');
    setStatus('sending');
    const ok = await saveSubmission({
      source: 'members_only_invite',
      email: method === 'email' ? email : undefined,
      phone: method === 'phone' ? `${code} ${phone}` : undefined,
      details: {
        method,
        consent: true
      }
    });
    if (ok) {
      setStatus('done');
    } else {
      setError(SUBMISSION_ERROR);
      setStatus('idle');
    }
  };
  const tab = (active: boolean) => `pb-1.5 text-[15px] transition-colors duration-200 ${active ? 'border-b border-ink font-medium text-ink' : 'border-b border-transparent font-light text-ink/55 hover:text-ink'}`;
  return <motion.div role="dialog" aria-modal="true" aria-labelledby="invite-wall-title" className="fixed inset-x-0 bottom-0 z-[70] border-t border-line bg-paper shadow-[0_-24px_60px_rgba(0,0,0,0.12)]" initial={{
    y: reduceMotion ? 0 : '100%',
    opacity: reduceMotion ? 0 : 1
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    duration: 0.3,
    ease
  }} data-id="element-593" data-mp="3b6omzyfg2klg">
      {onClose && <button type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center text-ink/40 transition-colors duration-150 hover:text-ink/80 md:right-6 md:top-5" data-id="element-594" data-mp="1n95o37qymr3z">
          <XIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-595" data-mp="1x0tjfjqn0saq" />
        </button>}
      <div className="mx-auto grid max-w-[1080px] grid-cols-1 gap-10 px-6 py-10 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:gap-16 md:px-10 md:py-14" data-id="element-596" data-mp="199efxnn4k4xw">
        <div data-id="element-597" data-mp="1iacazgfl524c">
          <p className="inline-flex items-center gap-2 text-[13px] font-light text-ink/60" data-id="element-598" data-mp="1iavsiw1bpohu">
            <LockIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-599" data-mp="1dagfk5kyej0e" />
            For members only
          </p>
          <h2 id="invite-wall-title" className="mt-4 font-editorial text-[34px] leading-[38px] tracking-[-0.01em] md:text-[44px] md:leading-[48px]" data-id="element-600" data-mp="2tnep84u7pflc">
            {title}
          </h2>
          <p className="mt-5 max-w-[440px] text-[16px] font-light leading-[25px] text-ink/75" data-id="element-601" data-mp="3k14krk4dr5cs">{body}</p>
        </div>

        <div className="md:pt-9" data-id="element-602" data-mp="3ozo8nq2jar7s">
          {status === 'done' ? <div role="status" data-id="element-603" data-mp="3txzi3zox2zph">
              <p className="font-editorial text-[28px] leading-[34px]" data-id="element-604" data-mp="3twxw506d4ssz">Received, in confidence.</p>
              <p className="mt-3 text-[15px] font-light leading-[24px] text-ink/75" data-id="element-605" data-mp="2t98hkaxd1rs">
                If an invitation is extended, you will hear from us — quietly, and only once.
              </p>
              <Link to="/" className="mt-7 inline-flex items-center gap-2 text-[15px] font-medium" data-id="element-606" data-mp="bqtc2zw9fa5k">
                Return to the house <span aria-hidden="true" data-id="element-607" data-mp="glp5a3d8g3k9">→</span>
              </Link>
            </div> : <form onSubmit={onSubmit} noValidate data-id="element-608" data-mp="lka4q0knuj7n">
              <div className="flex gap-6" role="tablist" aria-label="Contact method" data-id="element-609" data-mp="qik1gfm19rvd">
                <button type="button" role="tab" aria-selected={method === 'email'} onClick={() => {
              setMethod('email');
              setError('');
            }} className={tab(method === 'email')} data-id="element-610" data-mp="uicfv8y3ys8n">
                  Email
                </button>
                <button type="button" role="tab" aria-selected={method === 'phone'} onClick={() => {
              setMethod('phone');
              setError('');
            }} className={tab(method === 'phone')} data-id="element-611" data-mp="2axzzsfklrv34">
                  Phone
                </button>
              </div>

              <div className="mt-6 flex items-end border-b border-ink" data-id="element-612" data-mp="2yp5pdmgci1qm">
                {method === 'email' ? <>
                    <label htmlFor="invite-email" className="sr-only" data-id="element-613" data-mp="2tr3y4e8rmw42">
                      Email address
                    </label>
                    <input id="invite-email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email address" aria-invalid={!!error} className="w-full bg-transparent pb-3 text-[18px] placeholder:font-light placeholder:text-ink/50 focus:outline-none" data-id="element-614" data-mp="2ox9fzf8t6p73" />
                  </> : <>
                    <div className="pr-3" data-id="element-615" data-mp="110s4lsij1ao8">
                      <DialCodeSelect id="invite-code" value={code} onChange={setCode} className="pb-3 text-[18px]" data-id="element-616" data-mp="10zqqtga4ma2t" />
                    </div>
                    <span className="mb-3 mr-3 h-5 w-px bg-line" aria-hidden="true" data-id="element-617" data-mp="r2ccpnbodxdo" />
                    <label htmlFor="invite-phone" className="sr-only" data-id="element-618" data-mp="w305adfjyma0">
                      Phone number
                    </label>
                    <input id="invite-phone" type="tel" autoComplete="tel-national" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Your number" aria-invalid={!!error} className="min-w-0 flex-1 bg-transparent pb-3 text-[18px] placeholder:font-light placeholder:text-ink/50 focus:outline-none" data-id="element-619" data-mp="i4a70ugzygk2" />
                  </>}
              </div>
              {error && <p role="alert" className="mt-2 text-[14px] text-ink" data-id="element-620" data-mp="30dmvdn8cn5sl">
                  {error}
                </p>}

              <ConsentCheckbox id="invite-consent" checked={consent} onChange={v => {
            setConsent(v);
            if (v) setConsentError(false);
          }} error={consentError} className="mt-5" data-id="element-621" data-mp="2vjilow7p8jnt" />

              <button type="submit" disabled={status === 'sending'} className="mt-6 h-[54px] w-full bg-ink text-[16px] font-medium text-paper transition-opacity duration-150 hover:opacity-85 disabled:opacity-60" data-id="element-622" data-mp="3f8t26cf9r8mz">
                {status === 'sending' ? 'Sealing…' : 'Request an invitation'}
              </button>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 text-[14px] font-light text-ink/65" data-id="element-623" data-mp="2efcp2c65cnzb">
                <span data-id="element-624" data-mp="2j5jc47l0g0fg">Under NDA, always. Never shared.</span>
                <Link to="/login" className="transition-colors duration-150 hover:text-ink" data-id="element-625" data-mp="2j85rgte57k88">
                  Already a member? <span className="font-medium text-ink" data-id="element-626" data-mp="2n9hdyq1pzpc7">Log in</span>
                </Link>
              </div>
            </form>}
        </div>
      </div>
    </motion.div>;
}